/**
 * Janus PHP port calling convention (REF-R).
 * One export per PHP function, named as camelCase of its PHP name. Each carries
 * a `PHP: <name> (<file>:<line>)` doc line so census/deletion tools can find it.
 * By-reference parameters are PhpRef boxes; mutate .value, never replace the box.
 * PortEnv is the LAST parameter when needed. Preserve random-call order/bounds.
 * Call unowned functions without a TS twin through env.deps.<phpFunctionName>;
 * dependencies are real supplied callbacks. REF-R may replay captured PART0
 * STAY/orchestrator-allowlisted reader results; owned rules cannot be seeded.
 * Inline SQL readers take exact bound parameters followed by the normalized
 * SQL fingerprint computed by the port, never supplied by the replay callback.
 */
export type PhpRef<T> = { value: T };
/** Opaque oracle/runtime I/O identity. Read operations go through deps/io_sql. */
export const portDbHandleBrand: unique symbol = Symbol.for('FateTide.PortDbHandle');
export interface PortDbHandle {
    readonly [portDbHandleBrand]: true;
    readonly kind: 'PDO' | 'Database' | 'PDOStatement';
    readonly id: string;
}
/** Ordinary generators use numeric keys; this seam preserves explicit PHP keys. */
export const portGeneratorKey: unique symbol = Symbol.for('FateTide.PortGeneratorKey');
export function portKeyedGenerator<T, R>(entries: Generator<[string | number, T], R, unknown>): Generator<T, R, unknown> & { [portGeneratorKey]: string | number } {
    let value!: Generator<T, R, unknown> & { [portGeneratorKey]: string | number };
    value = Object.assign((function* () {
        let step = entries.next();
        while (!step.done) {
            value[portGeneratorKey] = step.value[0];
            const sent = yield step.value[1];
            step = entries.next(sent);
        }
        return step.value;
    })(), { [portGeneratorKey]: 0 as string | number });
    return value;
}
export interface PortEnv<Deps extends object = Record<string, (...args: never[]) => unknown>> {
    rand(min: number, max: number): number;
    /** Unix seconds, like time(). */
    now(): number;
    /** Unix seconds including fractional seconds, like microtime(true). */
    nowMicro(): number;
    /** PHP global names; static properties use Class::$prop. */
    globals: Record<string, unknown>;
    /** Function-local static slots, keyed by canonical PHP function::$variable. */
    functionStatics: Record<string, unknown>;
    out(text: string): void;
    uuid(): string;
    uniqid(prefix?: string, more?: boolean): string;
    exit(output?: string | number): never;
    /** Production diagnostics; compared as a set, excluding local-only 8.5 tags. */
    warn(message: string, severity?: number): void;
    randomBytes(length: number): Uint8Array;
    gmdate(format: string, timestamp?: number): string;
    hrtime(asNumber?: boolean): number | [number, number];
    /** Declare reader signatures in Deps, e.g. links(a: string, b: string): Row[].
     * STAY readers retain their PHP names; SQL readers retain their ts_dep name.
     * Both receive native page-reader shapes, with strict call order/arguments. */
    deps: Deps;
}
/** Returned alongside the value; the save lane applies it. REF-R writes:"record"
 * proves ordered intents locally; writes cannot be supplied as io_sql seeds. */
export interface PortWriteIntent {
    sql: string;
    params: unknown[] | Record<string, unknown>;
    /** Explicit PDO binding types, keyed by the original bound parameter. */
    param_types?: Record<string, number>;
}
export interface PortWithWrites<T> {
    readonly [portWritesBrand]: true;
    value: T;
    writes: PortWriteIntent[];
}
/** Symbol.for keeps the contract shared across independently bundled test ports. */
export const portWritesBrand: unique symbol = Symbol.for('FateTide.PortWithWrites');
export function portWithWrites<T>(value: T, writes: PortWriteIntent[]): PortWithWrites<T> {
    return { [portWritesBrand]: true, value, writes };
}
/** Same token normalization as REF-R's PHP oracle; preserve literals/identifiers. */
export function portSqlFingerprint(sql: string): string {
    const keywords = new Set(('select show describe desc explain from where join left right inner outer on as and or not null is in exists between like case when then else end order by asc group having limit offset union all distinct update insert delete replace into values set for lock share mode on duplicate key').split(' '));
    const tokens = sql.match(/'(?:''|\\.|[^'\\])*'|"(?:""|\\.|[^"\\])*"|`(?:``|[^`])*`|\/\*[\s\S]*?\*\/|--(?=[\x00-\x20])[^\n]*|#[^\n]*|[a-zA-Z_][a-zA-Z_0-9]*|\s+|./g) ?? [];
    let out = '';
    for (const token of tokens) {
        if (/^(?:\/\*|--(?=[\x00-\x20])|#)/.test(token) || /^\s+$/.test(token)) {
            if (!out.endsWith(' ')) out += ' ';
        } else out += keywords.has(token.toLowerCase()) ? token.toLowerCase() : token;
    }
    return out.trim().replace(/;+$/, '').trim();
}
/** Conservative table dependencies, including comma joins and schema reads. */
export function portSqlTables(sql: string): string[] {
    const fp = portSqlFingerprint(sql);
    if (/^show\s+(?:tables|table\s+status)\b/i.test(fp)) return ['*'];
    const tokens = fp.match(/`(?:``|[^`])*`|'(?:''|\\.|[^'\\])*'|"(?:""|\\.|[^"\\])*"|[a-zA-Z_][a-zA-Z_0-9]*|[.,()]/g) ?? [];
    const boundary = new Set('where set on using join left right inner outer cross natural group order having limit union values returning for lock'.split(' '));
    const identifier = (token: string | undefined): token is string => token !== undefined && /^(?:`|[a-zA-Z_])/.test(token) && !boundary.has(token.toLowerCase());
    const tables = new Set<string>();
    tokens.forEach((token, i) => {
        const marker = token.toLowerCase();
        if (!['from', 'join', 'update', 'insert', 'replace', 'into', 'describe', 'desc'].includes(marker)
            && !(marker === 'table' && tokens[i - 1]?.toLowerCase() === 'create' && tokens[i - 2]?.toLowerCase() === 'show')) return;
        let j = i + 1;
        if (['update', 'insert', 'replace'].includes(marker)) while (['low_priority', 'high_priority', 'delayed', 'ignore', 'into'].includes(tokens[j]?.toLowerCase() ?? '')) j++;
        while (identifier(tokens[j])) {
            let name = tokens[j++]!;
            if (tokens[j] === '.' && identifier(tokens[j + 1])) { name = tokens[j + 1]!; j += 2; }
            tables.add(name.replace(/^`|`$/g, '').replace(/``/g, '`'));
            if (tokens[j]?.toLowerCase() === 'as') j++;
            if (identifier(tokens[j])) j++;
            if (tokens[j] !== ',') break;
            j++;
        }
    });
    return [...tables];
}
/** Read dependencies include ambiguous qualified identifiers, never string literals. */
export function portSqlReadNames(sql: string): string[] {
    const tokens = portSqlFingerprint(sql).match(/`(?:``|[^`])*`|'(?:''|\\.|[^'\\])*'|"(?:""|\\.|[^"\\])*"|[a-zA-Z_][a-zA-Z_0-9]*/g) ?? [];
    return tokens.filter(name => !name.startsWith("'") && !name.startsWith('"')).map(name => name.replace(/^`|`$/g, '').replace(/``/g, '`'));
}
/** Validate raw SQL before normalization can erase executable comments. */
export function portSqlWriteFingerprint(sql: string): string {
    const unquoted = sql.replace(/'(?:''|\\.|[^'\\])*'|"(?:""|\\.|[^"\\])*"|`(?:``|[^`])*`/g, '');
    if (/\/\*|--(?=[\x00-\x20])|#/.test(unquoted) || /\r(?!\n)/.test(sql) || /(?![\x00-\x7f])[\p{Z}\u0085\uFEFF]/u.test(sql))
        throw new Error('IO_SQL_WRITE');
    const raw = unquoted;
    if ((raw.match(/;/g)?.length ?? 0) > 1 || /;\s*\S/.test(raw)) throw new Error('IO_SQL_WRITE');
    const fingerprint = portSqlFingerprint(sql);
    const bare = fingerprint.replace(/'(?:''|\\.|[^'\\])*'|"(?:""|\\.|[^"\\])*"|`(?:``|[^`])*`/g, '');
    if (!/^(?:insert|update|delete|replace)\b/i.test(bare)
        || /;|@|\b(?:alter|drop|create|truncate|call|lock|unlock|begin|commit|rollback|savepoint|outfile|dumpfile|load_file|get_lock|release_lock)\b/i.test(bare))
        throw new Error('IO_SQL_WRITE');
    return fingerprint;
}
/** Capture preflight mirrors the oracle before a request reaches the database. */
export function portSqlReadFingerprint(sql: string): string {
    const unquoted = sql.replace(/'(?:''|\\.|[^'\\])*'|"(?:""|\\.|[^"\\])*"|`(?:``|[^`])*`/g, '');
    if (/\/\*(?:!|M!|\+)/i.test(unquoted) || /\r(?!\n)/.test(sql) || /(?![\x00-\x7f])[\p{Z}\u0085\uFEFF]/u.test(sql)) throw new Error('IO_SQL_WRITE');
    const raw = unquoted.replace(/\/\*[\s\S]*?\*\/|--(?=[\x00-\x20])[^\n]*|#[^\n]*/g, '');
    if ((raw.match(/;/g)?.length ?? 0) > 1 || /;\s*\S/.test(raw)) throw new Error('IO_SQL_WRITE');
    const fp = portSqlFingerprint(sql);
    if (/^show\b/i.test(fp) && !/^show\s+(?:columns|fields|index|keys|create\s+table|tables|table\s+status)\b/i.test(fp)) throw new Error('IO_SQL_WRITE');
    if (portSqlReadNames(sql).some(name => ['information_schema', 'performance_schema', 'mysql', 'sys'].includes(name.toLowerCase()))
        || /"(?:information_schema|performance_schema|mysql|sys)"\s*\./i.test(fp)) throw new Error('IO_SQL_SENSITIVE');
    const bare = fp.replace(/'(?:''|\\.|[^'\\])*'|"(?:""|\\.|[^"\\])*"|`(?:``|[^`])*`/g, '');
    const checked = bare.replace(/^show\s+create\s+table\b/i, 'show metadata');
    const explain = /^explain\b/i.test(bare);
    if (!/^(?:select|show|describe|desc|explain)\b/i.test(bare)
        || /;|@|\b(?:alter|drop|create|truncate|call|begin|commit|rollback|savepoint|outfile|dumpfile|load_file|for\s+(?:update|share)|nowait|skip\s+locked|lock\s+in\s+share|sleep|benchmark|get_lock|release_lock)\b/i.test(checked)
        || !explain && /\b(?:insert|update|delete|replace|into)\b/i.test(checked) || explain && /\banalyze\b/i.test(checked)) throw new Error('IO_SQL_WRITE');
    const identifiers = fp.replace(/'(?:''|\\.|[^'\\])*'|"(?:""|\\.|[^"\\])*"/g, '');
    if (/\b(?:[a-z_0-9]*(?:password|passwd|salt|email|session|anatomy|credential|auth_token)[a-z_0-9]*|accounts|users|user|logins|login_throttle|user_entitlements|sessions|anatomies|special_keys)\b/i.test(identifiers)) throw new Error('IO_SQL_SENSITIVE');
    return fp;
}
export interface BrowserPortRollSource {
    d(die: number): number;
    fraction(): number;
}
/** Inclusive [min,max]: min + d(max-min+1) - 1 for widths supported by d.
 * Widths above 2^32 (or the source's maximum die) throw: fraction scaling
 * cannot preserve PHP's uniform integer distribution.
 * Integer faces must be in [1,width]. Even a width
 * of one consumes a draw, preserving PHP's random-call tape.
 */
export function browserPortRand(source: BrowserPortRollSource, maxDie = 0x1_0000_0000): PortEnv['rand'] {
    return (min, max) => {
        const width = max - min + 1;
        if (!Number.isSafeInteger(min) || !Number.isSafeInteger(max) || !Number.isSafeInteger(width) || width < 1)
            throw new RangeError('Invalid PHP rand bounds');
        if (width > Math.min(maxDie, 0x1_0000_0000)) throw new RangeError('PHP rand width exceeds 2^32 or source maximum');
        const face = source.d(width);
        if (!Number.isSafeInteger(face) || face < 1 || face > width) throw new RangeError('Invalid browser random draw');
        return min + face - 1;
    };
}
